import { sectionRenderer } from '@theme/section-renderer';

class ProductAPIData {
  constructor() {
    this.addToCartButton = document.querySelector('.add-to-cart');
    this.cartItemsId = document.querySelector('cart-items-component').dataset.sectionId;
    this.variants = document.querySelectorAll('.section-variants .variant-values');
    this.mediaGallery = document.querySelector('.voo-main-product__media media-gallery-infinite');
    this.variantImageId = this.mediaGallery.querySelector('.voo-variant-image')?.dataset.mediaId;
    this.dynamicImages = document.querySelectorAll('li.voo-dynamic-image');
    this.imageBaseSrc = '//primeeva.myshopify.com/cdn/shop/files/';
    this.cartDrawer = document.querySelector('cart-drawer-component');
    this.apiUrl = document.querySelector('.voo-main-product[data-api]').dataset.api;
    this.preloader = document.querySelector('.preloader-ring');
    this.errorField = document.querySelector('.error-message');
    this.defaultConfigImgSrc = document.querySelector('ul.product__media-list li:nth-of-type(3) img').src;
    this.magnifyImageEl = document.querySelector('.product-media-modal__content img:nth-of-type(3)');
    this.carSelectorElement = document.querySelector('.car-selectors');
    this.progressBarElement = document.querySelector('.selector-progress');
    this.progressBarLine = this.progressBarElement?.querySelector('.progress-line');
    this.notSureButtons = document.querySelectorAll('.not-sure');
    this.notSureMessage = document.querySelector('.not-sure-message');
    this.currentStep = 0;
    this.totalSteps = document.querySelectorAll('.car-selectors [data-step]').length;
    this.dynamicProducts = window.Shopify?.dynamicProductRedirects || {};
    this.carSelectors = {
      make: this.carBrand,
      model: this.carModel,
      generation: this.carGeneration,
      transmission: this.carTransmisson,
      body_type: this.carBody,
    };
    this.needToRedirect = { redirect: false, redirect_url: '', car_type: '' };
    this.searchParamsLoaded = false;
    this.carSelectorsUpdating = true;
    this.customCarParameters = { make: 0, model: 0, generation: 0, transmission: 0, body_type: 0 };
    this.translations = {
      search: window.Shopify?.translations?.product?.search || 'Search',
      searchPlaceholder: window.Shopify?.translations?.product?.search_placeholder || 'Search or add yourself',
      addToListText: window.Shopify?.translations?.product?.add_to_list || 'Add to List',
      select: window.Shopify?.translations?.product?.select || 'Select',
    };
    this.cacheKeys = {
      make: 'voo_car_make_cache',
      makeTimestamp: 'voo_car_make_cache_timestamp',
    };
    this.cacheExpiration = 7 * 24 * 60 * 60 * 1000;
  }

  /**
   * @param {number} step
   * @param {number} totalSteps
   */
  updateProgressBar(step, totalSteps) {
    if (!(this.progressBarLine instanceof HTMLElement)) return;
    const stepNumberEl = this.progressBarElement?.querySelector('.step-number');
    const totalStepsEl = this.progressBarElement?.querySelector('.total-steps');
    if (stepNumberEl instanceof HTMLElement) stepNumberEl.textContent = step.toString();
    if (totalStepsEl instanceof HTMLElement) totalStepsEl.textContent = totalSteps.toString();

    this.currentStep = step;
    const progress = (step / totalSteps) * 100;
    this.progressBarLine.style.transform = `scaleX(${progress}%)`;
  }

  /**
   * @param {number} step
   */
  openSelectorByStep(step) {
    if (this.carSelectorsUpdating) return;
    let nextStep = Number(step) + 1;
    const stepElement = this.carSelectorElement?.querySelector(`.nice-select[data-step="${nextStep}"]`);
    if (stepElement instanceof HTMLElement) {
      stepElement.focus();
      window.setTimeout(() => {
        stepElement.classList.add('open');
      }, 200);
    }
  }

  /**
   * @param {string} lastType
   */
  updateCarTitleUpTo(lastType) {
    let titleParams = '';
    for (const type of ['make', 'model', 'generation', 'body_type', 'transmission']) {
      if (type == lastType) break;
      const optionTitle = document.querySelector(
        `#voo-car-${type} + .nice-select li.selected:not(.disabled)`,
      )?.innerHTML;
      if (optionTitle != 'Nicht sicher') titleParams += type === 'body_type' ? optionTitle : optionTitle + ' ';
    }
    document.querySelectorAll('.voo-product-selected').forEach((title) => (title.innerHTML = titleParams));
  }

  updateSearchParams() {
    let selectedTypeParam = '';
    for (const [key, value] of Object.entries(this.carSelectors)) {
      if (document.getElementById(`voo-car-${key}`)) {
        selectedTypeParam = document.getElementById(`voo-car-${key}`)?.dataset.value || '';
      }
      if (selectedTypeParam != '') {
        this.searchParams?.set(key, selectedTypeParam);
      } else {
        if (this.searchParamsLoaded) {
          this.searchParams?.delete(key);
        }
      }
    }
    window.history.replaceState(
      {},
      '',
      window.location.origin + window.location.pathname + `?${this.searchParams?.toString()}`,
    );

    // Don't set searchParamsLoaded to true if we're still loading from URL
    // Check if all URL parameters have corresponding selector values
    if (this.searchParams && this.searchParams.get('make')) {
      const urlParams = ['make', 'model', 'generation', 'body_type', 'transmission'];
      const urlParamChecks = urlParams.map((type) => {
        const urlValue = this.searchParams.get(type);
        if (!urlValue) {
          return { type, hasValue: true, reason: 'no URL param' };
        }
        const selector = document.getElementById(`voo-car-${type}`);
        const selectorValue = selector?.dataset.value;
        const hasValue = selectorValue && selectorValue === urlValue;
        return { type, urlValue, selectorValue, hasValue };
      });

      const allUrlParamsHaveValues = urlParamChecks.every((check) => check.hasValue);

      if (!allUrlParamsHaveValues) {
        // Still loading from URL, don't set searchParamsLoaded yet
        return; // Don't set searchParamsLoaded
      }
    }

    this.searchParamsLoaded = true;
  }

  /**
   * @param {boolean} disable
   */
  toggleAddToCartButton(disable) {
    if (!(this.addToCartButton instanceof HTMLElement)) return;
    if (disable) {
      this.addToCartButton.setAttribute('disabled', 'true');
      if (this.addToCartButton.dataset.notSelectedText)
        this.addToCartButton.innerHTML = this.addToCartButton.dataset.notSelectedText;
    } else {
      this.addToCartButton.removeAttribute('disabled');
      if (this.addToCartButton.dataset.text) this.addToCartButton.innerHTML = this.addToCartButton.dataset.text;
    }
  }

  /**
   * @param {HTMLElement} button
   */
  handleNotSureClick(button) {
    const currentSelector = button.closest('.nice-select');
    const notSureOption = currentSelector?.querySelector('[data-value="notSure"]');
    if (!(button instanceof HTMLElement) || !(notSureOption instanceof HTMLElement)) return;
    currentSelector?.classList.add('not-sure-selected');
    const placeholder =
      currentSelector?.querySelector('.hint') || currentSelector?.querySelector('.current .nice-option');
    const selectDefault = currentSelector?.previousElementSibling;
    const type = selectDefault?.id.split('voo-car-')[1];
    const step = Number(currentSelector?.dataset.step);
    this.clearSelectors(type);
    if (step) {
      this.updateProgressBar(step, step);
      this.clearAllNextSelectors(step);
    }
    if (type) this.updateCarTitleUpTo(type);

    if (notSureOption instanceof HTMLElement) {
      notSureOption.classList.remove('disabled');
      notSureOption.classList.add('selected');
      if (selectDefault instanceof HTMLElement) selectDefault.dataset.value = 'notSure';
      if (placeholder) {
        placeholder.innerHTML = notSureOption.innerHTML;
        placeholder.classList.add('nice-option');
      }

      if (type === 'generation') {
        const bodyTypeSelector = document.querySelector('#voo-car-body_type')?.nextElementSibling;
        const transmissionSelector = document.querySelector('#voo-car-transmission')?.nextElementSibling;
        const bodyTypeParam = !this.searchParamsLoaded ? this.searchParams?.get('body_type') : null;
        const transmissionParam = !this.searchParamsLoaded ? this.searchParams?.get('transmission') : null;

        if (bodyTypeSelector && (!bodyTypeParam || bodyTypeParam === 'notSure')) {
          const bodyTypeNotSureOption = bodyTypeSelector.querySelector('[data-value="notSure"]');
          const bodyTypePlaceholder =
            bodyTypeSelector.querySelector('.hint') || bodyTypeSelector.querySelector('.current .nice-option');

          if (bodyTypeNotSureOption) {
            bodyTypeSelector.classList.add('not-sure-selected');
            bodyTypeNotSureOption.classList.remove('disabled');
            bodyTypeNotSureOption.classList.add('selected');
            if (bodyTypePlaceholder) {
              bodyTypePlaceholder.innerHTML = bodyTypeNotSureOption.innerHTML;
              bodyTypePlaceholder.classList.add('nice-option');
            }
            this.customCarParameters.body_type = 1;
          }
        }

        if (transmissionSelector && (!transmissionParam || transmissionParam === 'notSure')) {
          const transmissionNotSureOption = transmissionSelector.querySelector('[data-value="notSure"]');
          const transmissionPlaceholder =
            transmissionSelector.querySelector('.hint') || transmissionSelector.querySelector('.current .nice-option');

          if (transmissionNotSureOption) {
            transmissionSelector.classList.add('not-sure-selected');
            transmissionNotSureOption.classList.remove('disabled');
            transmissionNotSureOption.classList.add('selected');
            if (transmissionPlaceholder) {
              transmissionPlaceholder.innerHTML = transmissionNotSureOption.innerHTML;
              transmissionPlaceholder.classList.add('nice-option');
            }
            this.customCarParameters.transmission = 1;
          }
        }
      }

      if (type && this.customCarParameters[type] !== undefined) {
        this.customCarParameters[type] = 1;
      }

      this.toggleAddToCartButton(false);
      this.toggleNotSureMessage(true);
      if (this.searchParamsLoaded) {
        this.updateSearchParams();
      } else {
        if (selectDefault instanceof HTMLElement && selectDefault.dataset.value) {
          this.searchParams?.set(type, selectDefault.dataset.value);
        }

        if (type === 'generation' && !this.searchParamsLoaded) {
          const bodyTypeParam = this.searchParams?.get('body_type');
          const transmissionParam = this.searchParams?.get('transmission');

          if (bodyTypeParam && bodyTypeParam !== 'notSure') {
            const bodyTypeSelector = document.getElementById('voo-car-body_type');
            if (bodyTypeSelector) {
              this.updateSelectors(bodyTypeSelector, 'body_type', selectDefault.dataset.value);
            }
          }

          if (transmissionParam && transmissionParam !== 'notSure') {
            const transmissionSelector = document.getElementById('voo-car-transmission');
            if (transmissionSelector) {
              setTimeout(() => {
                this.updateSelectors(transmissionSelector, 'transmission', selectDefault.dataset.value);
              }, 100);
            }
          }
        }

        const nextStepSelect = currentSelector?.nextElementSibling;
        if (nextStepSelect && nextStepSelect.dataset.type && !this.searchParamsLoaded) {
          const nextType = nextStepSelect.dataset.type;
          const nextSelector = document.getElementById(`voo-car-${nextType}`);
          const nextParamValue = this.searchParams.get(nextType);
          if (nextSelector && nextParamValue && (nextParamValue !== 'notSure' || type !== 'generation')) {
            this.updateSelectors(nextSelector, nextType, selectDefault.dataset.value);
          }
        }
      }
      currentSelector?.classList.remove('open');
      const productControls = document.querySelectorAll('.voo-product-controls');
      if (productControls.length > 0) {
        productControls.forEach((control) => control.removeAttribute('disabled'));
      }
      document.querySelectorAll('.voo-product-controls')?.forEach((control) => control.removeAttribute('disabled'));
    }
  }

  /**
   * @param {number} step
   */
  clearAllNextSelectors(step) {
    const niceSelectors = document.querySelectorAll('.nice-select');
    niceSelectors.forEach((niceSelect) => {
      if (niceSelect && niceSelect.dataset.step >= step) {
        niceSelect.previousElementSibling.dataset.value = '';
        niceSelect.querySelectorAll('li').forEach((li) => {
          if (li.dataset.notSure) {
            li.classList.add('disabled');
          }
          li.classList.remove('selected');
        });
      }
    });
  }

  /**
   * @param {boolean} boolean
   */
  toggleNotSureMessage(boolean) {
    if (boolean === false) {
      this.notSureMessage instanceof HTMLElement && (this.notSureMessage.style.display = 'none');
    } else {
      this.notSureMessage instanceof HTMLElement && (this.notSureMessage.style.display = 'block');
    }
  }

  getVariantsData() {
    if (!this._variantsDataCache) {
      const el =
        document.querySelector('#variants_json[type="application/json"]') || document.querySelector('#variants_json');
      this._variantsDataCache = el ? JSON.parse(el.textContent) : [];
    }
    return this._variantsDataCache;
  }

  getSecondaryImagesData() {
    if (!this._secondaryImagesCache) {
      const el = document.querySelector('#secondaryImages');
      this._secondaryImagesCache = el ? JSON.parse(el.innerHTML) : [];
    }
    return this._secondaryImagesCache;
  }

  getEquipmentVariantsData() {
    const script = document.querySelector('.product-type-switcher.selected script');
    const raw = script?.textContent?.trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  setLocalStorageOptions() {
    if (this.variants.length == 0) return;

    this.variants.forEach((variant) => {
      variant.querySelectorAll('input').forEach((input) => {
        if (input.checked === true) {
          localStorage.setItem(variant.dataset.option, input.dataset.color);
        }
      });
    });
  }

  async updateImageSrc(image) {
    let imageSrc = '';
    let imageAlt = '';
    const useVariantImage = image.closest('.voo-dynamic-image')?.classList.contains('voo-variant-image');
    const useObjectImage = image.closest('.voo-dynamic-image')?.classList.contains('voo-metaobject-image');
    const textureCode = this.variants[0].querySelector('input:checked').dataset.color;
    const matCode = this.variants[1].querySelector('input:checked').dataset.color;
    const borderCode = this.variants[2].querySelector('input:checked').dataset.color;
    const secondaryImagesObject = this.getSecondaryImagesData();
    const variantsData = this.getVariantsData();
    const variantId = new URLSearchParams(window.location.search).get('variant');
    const variant = variantsData.find((variant) => variant.id == variantId);

    if (useVariantImage && variant) {
      imageSrc = `${variant.featured_image.src.replace('.com', '.myshopify.com')}&width=${image.getAttribute('width')}`;
      imageAlt = `${variant.featured_image.alt}`;
    } else if (useObjectImage) {
      const currentImage = secondaryImagesObject.find(
        (item) => item.mat === matCode && item.border === borderCode && item.texture === textureCode,
      );

      imageSrc = currentImage
        ? currentImage.url
        : `${this.imageBaseSrc}${index}-${textureCode}-${borderCode}-${matCode}.jpg?width=${image.getAttribute('width')}`;
      imageAlt = currentImage ? currentImage.alt : '';
    } else return;

    if (!(textureCode || borderCode || matCode)) {
      console.error('Some of picture params not exist');
      return;
    }

    // Optimistic swap: set src directly and let the browser load the new image.
    // If the URL 404s (not every color × texture × border combo is uploaded to the CDN),
    // restore the previous src via onerror. This trades a possible flash of broken-image
    // for instant visual feedback — vs. the old approach which always blocked on a
    // HEAD-like fetch before swapping (~150–500 ms of perceived lag per variant click).
    /**
     * @param {HTMLImageElement | null | undefined} imgEl
     * @param {string} newSrc
     * @param {string} newAlt
     */
    const swap = (imgEl, newSrc, newAlt) => {
      if (!imgEl) return;
      const currentSrcBase = imgEl.src.split('?')[0];
      if (currentSrcBase === newSrc.split('?')[0]) return;
      const prevSrc = imgEl.src;
      const prevSrcset = imgEl.srcset;
      const onError = () => {
        imgEl.src = prevSrc;
        if (prevSrcset) imgEl.srcset = prevSrcset;
      };
      imgEl.addEventListener('error', onError, { once: true });
      imgEl.src = newSrc;
      if (newAlt) imgEl.alt = newAlt;
    };

    const mainSrcBase = image.src.split('?')[0];

    const zoomImages = /** @type {NodeListOf<HTMLImageElement>} */ (
      document.querySelectorAll('product-modal .product-media-modal__content img')
    );
    zoomImages.forEach((zoomImage) => {
      if (mainSrcBase === zoomImage.src.split('?')[0]) {
        const zoomQuery = zoomImage.src.split('?')[1] || '';
        const nextSrc = zoomQuery ? `${imageSrc}?${zoomQuery}` : imageSrc;
        swap(zoomImage, nextSrc, imageAlt);
        zoomImage.srcset = `${imageSrc}?width=550 550w,${imageSrc} 1000w`;
      }
    });

    const thumbImages = /** @type {NodeListOf<HTMLImageElement>} */ (
      document.querySelectorAll('.voo-thumbnail .thumbnail-slider img')
    );
    thumbImages.forEach((thumbImage) => {
      if (mainSrcBase === thumbImage.src.split('?')[0]) {
        const thumbQuery = thumbImage.src.split('?')[1] || '';
        const nextSrc = thumbQuery ? `${imageSrc}?${thumbQuery}` : imageSrc;
        swap(thumbImage, nextSrc, imageAlt);
        thumbImage.srcset = `${imageSrc}?width=550 550w,${imageSrc} 1000w`;
      }
    });

    swap(image, imageSrc, imageAlt);
  }

  handleSelectorLoading(scroll = true) {
    if (scroll) {
      const carParams = new URLSearchParams(window.location.search);
      if (carParams.get('pos')) window.scrollTo(0, Number(carParams.get('pos')));
    }
    this.enableCarSwitchers();

    this.carSelectorsUpdating = false;
    document.querySelector('.voo-back-data').style.minHeight = 'auto';
  }

  clearSelectors(type) {
    let isNeedToUpdate = false;
    if (!type) {
      isNeedToUpdate = true;
    }
    document.querySelectorAll('.nice-select').forEach((niceSelect) => {
      if (niceSelect.previousElementSibling.id == `voo-car-${type}`) {
        isNeedToUpdate = true;
      } else if (isNeedToUpdate) {
        this.carSelectors[niceSelect.previousElementSibling.id.split('voo-car-')[1]].update();
      }
    });
  }

  enableCarSwitchers() {
    document.querySelectorAll('.product-type-switcher:not(.selected)').forEach((switcher) => {
      switcher.dataset.disabled = 'false';
    });
  }

  validateCarParams(obj) {
    const keysToValidate = ['make', 'model', 'generation', 'body_type', 'transmission'];
    for (let [key, value] of Object.entries(obj)) {
      if (keysToValidate.some((element) => element === key) && !Number.isInteger(+value)) return false;
    }
    return true;
  }

  async updateSelectors(selector, type, id = false) {
    this.errorField.style.display = 'none';
    let carParameters = {
      locale: document.querySelector('html').getAttribute('lang'),
    };
    // if car have previous params set to carParameters object
    if (id) {
      ['make', 'model', 'generation', 'body_type', 'transmission'].forEach((el) => {
        if (document.querySelector(`.voo-back-data select[data-type="${el}"]`).dataset.value) {
          carParameters[`${el}`] = document.querySelector(`.voo-back-data select[data-type="${el}"]`).dataset.value;
        }
      });
    }

    // Get placeholder for nice select
    let placeholder = selector.dataset
      ? selector.dataset.placeholder
      : document.getElementById(`${selector}`).dataset.placeholder;

    // Options for nice select
    let niceSelectOptions = {
      placeholder: placeholder,
      searchPlaceholder: this.translations.searchPlaceholder,
      searchable: true,
      selectText: this.translations.select,
    };

    // if has car pametrs in url
    this.searchParams = new URLSearchParams(window.location.search);
    if (!this.searchParams.get('transmission')) {
      this.preloader instanceof HTMLElement ? (this.preloader.style.display = 'none') : null;
      this.carSelectorElement.style.display = 'flex';
      this.handleSelectorLoading(false);
    }

    if (type) {
      let dataPromise;

      if (type === 'make') {
        const cachedData = this.getCachedMakeData();
        if (cachedData) {
          dataPromise = cachedData;
        } else {
          let validated = this.validateCarParams(carParameters);
          dataPromise = validated
            ? this.getCarData(this.apiUrl + type, carParameters)
                .then((res) => res.json())
                .then((data) => {
                  this.setCachedMakeData(data);
                  return data;
                })
            : Promise.resolve({ data: [], status: true, extra_data: [] });
        }
      } else {
        let validated = this.validateCarParams(carParameters);
        dataPromise = validated
          ? this.getCarData(this.apiUrl + type, carParameters).then((res) => res.json())
          : Promise.resolve({ data: [], status: true, extra_data: [] });
      }

      dataPromise
        .then((data) => {
          if (type == 'make') {
            // Update nice select and add groups
            let optionGroupPopular = document.createElement('optgroup');
            optionGroupPopular.class = 'popular-car';
            optionGroupPopular.label = 'Beliebte Automarken';
            let optionGroupOther = document.createElement('optgroup');
            optionGroupOther.class = 'other-car';
            optionGroupOther.label = 'Andere Autos';
            selector.appendChild(optionGroupPopular);
            selector.appendChild(optionGroupOther);

            // Load data to select and group to popular and other
            data.data.forEach((option) => {
              let optionElement = document.createElement('option');
              optionElement.value = option.id;
              optionElement.innerText = option.name;

              if (option.is_popular == 1) {
                optionElement.classList.add('popular');
                optionGroupPopular.appendChild(optionElement);
              } else {
                optionGroupOther.appendChild(optionElement);
              }
            });

            // After adding data update nice select
            this.carSelectors[type].update();
            selector.nextElementSibling.classList.remove('error-data');

            // Clear another selectors if data were loaded
            this.clearSelectors(type);

            // Add data attribute to selector
            if (id)
              document.querySelector(`.voo-back-data select[data-type="${type}"]`).setAttribute('data-value', `${id}`);

            // If have url params and this params not loaded -> click to next select to load next param
            if (this.searchParams.get(type) && !this.searchParamsLoaded) {
              setTimeout(() => {
                const paramValue = this.searchParams.get(type);
                const notSureOption = document.querySelector(
                  `#voo-car-${type} + .nice-select li[data-value="notSure"]`,
                );
                const regularOption = document.querySelector(
                  `#voo-car-${type} + .nice-select li[data-value="${paramValue}"]`,
                );

                if (paramValue === 'notSure' && notSureOption) {
                  const notSureButton =
                    document.querySelector(`#voo-car-${type} + .nice-select .not-sure`) || notSureOption;
                  this.handleNotSureClick(notSureButton);
                } else if (regularOption) {
                  regularOption.click();
                } else {
                  selector.nextElementSibling.querySelector('input.nice-select-search').value = paramValue;

                  let newOption = document.createElement('option');
                  newOption.textContent = paramValue;
                  newOption.classList.add('custom');
                  selector.querySelector('option.hidden').after(newOption);
                  // Update selector and clear others
                  this.carSelectors[type].update();
                  this.clearSelectors(type);

                  // Update custom paramets
                  this.customCarParameters[type] = 1;

                  // Select new param
                  selector.nextElementSibling.classList.remove('error-data');
                  selector.nextElementSibling.querySelector(`.option[data-value="${paramValue}"]`).click();
                }
                // by default after click nice select is opening so i close it
                selector.nextElementSibling.classList.remove('open');
              }, 50);
            }
          } else {
            // Clean from last data if had except of default param
            selector.querySelectorAll('option').forEach((el) => {
              if (!el.classList.contains('hidden')) el.remove();
            });

            if (!data.data || data.data.length === 0) {
              this.toggleAddToCartButton(true);
              const productControls = document.querySelectorAll('.voo-product-controls');
              if (productControls.length > 0) {
                productControls.forEach((control) => control.removeAttribute('disabled'));
              }

              let titleParams = '';
              ['make', 'model', 'generation', 'body_type', 'transmission'].forEach((type, i) => {
                const selectedTypeParam = document.getElementById(`voo-car-${type}`).dataset.value;
                if (selectedTypeParam) {
                  this.searchParams.set(type, selectedTypeParam);
                  const optionTitle = document.querySelector(
                    `#voo-car-${type} + .nice-select li.selected:not(.disabled)`,
                  )?.innerHTML;
                  if (optionTitle) titleParams += i === 4 ? optionTitle : optionTitle + ' ';
                } else {
                  this.searchParams.delete(type);
                }
              });
              document.querySelectorAll('.voo-product-selected').forEach((title) => (title.innerHTML = titleParams));
              window.history.replaceState(
                {},
                '',
                window.location.origin + window.location.pathname + `?${this.searchParams.toString()}`,
              );
              this.searchParamsLoaded = true;
              this.getAndUpdateAdditionalData(this.searchParams.toString());
            }

            // Load data to select
            if (data.data) {
              data.data.forEach((option) => {
                let optionElement = document.createElement('option');
                optionElement.value = option.id;
                optionElement.innerText = option.name_de ? option.name_de : option.name;
                selector.appendChild(optionElement);
              });
            }
            // if transmission check if product the same type
            // If not set redirect url and last step will be redirect to new type with car params
            if (type == 'body_type' && data.data && data.data.length > 0) {
              if (
                data.data[0].car_category_key &&
                data.data[0].car_category_key !== document.querySelector('.voo-main-product[data-domain]').dataset.type
              ) {
                this.needToRedirect.redirect = true;
                let matched = false;
                for (const key in this.dynamicProducts) {
                  if (`${key}` === data.data[0].car_category_key) {
                    this.needToRedirect.redirect_url = this.dynamicProducts[key];
                    this.needToRedirect.car_type = key;
                    matched = true;
                    break;
                  }
                }

                if (!matched) {
                  this.needToRedirect.redirect_url = this.dynamicProducts.standard;
                  this.needToRedirect.car_type = 'regular';
                  if (document.querySelector('.voo-main-product[data-template]').dataset.template === 'regular') {
                    this.needToRedirect.redirect = false;
                  }
                }
              } else {
                this.needToRedirect.redirect = false;
              }
            }

            // After adding data update nice select
            this.carSelectors[type].update();

            // Clear another selectors if data were loaded
            this.clearSelectors(type);

            // If have url params -> click to next select to load next param
            if (this.searchParams.get(type) && !this.searchParamsLoaded) {
              setTimeout(() => {
                const paramValue = this.searchParams.get(type);
                const notSureOption = document.querySelector(
                  `#voo-car-${type} + .nice-select li[data-value="notSure"]`,
                );
                const regularOption = document.querySelector(
                  `#voo-car-${type} + .nice-select li[data-value="${paramValue}"]`,
                );

                if (paramValue === 'notSure' && notSureOption) {
                  const notSureButton =
                    document.querySelector(`#voo-car-${type} + .nice-select .not-sure`) || notSureOption;
                  this.handleNotSureClick(notSureButton);
                } else if (regularOption) {
                  regularOption.click();
                } else {
                  selector.nextElementSibling.querySelector('input.nice-select-search').value = paramValue;

                  let newOption = document.createElement('option');
                  newOption.textContent = paramValue;
                  newOption.classList.add('custom');
                  selector.querySelector('option.hidden').after(newOption);
                  // Update selector and clear others
                  this.carSelectors[type].update();
                  this.clearSelectors(type);

                  // Update custom paramets
                  this.customCarParameters[type] = 1;

                  // Select new param
                  selector.nextElementSibling.classList.remove('error-data');
                  selector.nextElementSibling.querySelector(`.option[data-value="${paramValue}"]`).click();
                }

                // by default after click nice select is opening so i close it
                selector.nextElementSibling.classList.remove('open');
              }, 100);
            }

            // Remove selected from updated select
            selector.nextElementSibling.querySelector('li.option:not(.disabled)')?.classList.remove('selected');
            selector.nextElementSibling.classList.remove('error-data');
          }
        })
        .catch((error) => {
          console.error('Error loading', error);
          this.errorField.style.display = 'block';
          this.preloader.style.display = 'none';
        });
    } else {
      const select = document.getElementById(`${selector}`);
      select.classList.add('error-data');
      // Initialize epmty nice selectors
      for (const key in this.carSelectors) {
        if (selector == `voo-car-${key}`) {
          if (key == 'make') {
            niceSelectOptions.searchPlaceholder = this.translations.search;
          } else {
            niceSelectOptions.searchPlaceholder = this.translations.searchPlaceholder;
          }
          niceSelectOptions.addButtonText = this.translations.addToListText;
          niceSelectOptions.selectText = this.translations.select;
          this.carSelectors[key] = NiceSelect.bind(select, niceSelectOptions);
        }
      }
    }
  }

  getCachedMakeData() {
    try {
      if (window.CarMakesData && window.CarMakesData.data && Array.isArray(window.CarMakesData.data)) {
        return Promise.resolve({
          data: window.CarMakesData.data,
          status: window.CarMakesData.status !== undefined ? window.CarMakesData.status : true,
          extra_data: window.CarMakesData.extra_data || [],
        });
      }

      const inlineData = document.querySelector('#car-makes-data');
      if (inlineData) {
        const data = JSON.parse(inlineData.textContent);
        if (data && data.data && Array.isArray(data.data)) {
          return Promise.resolve({ data: data.data, status: true, extra_data: [] });
        }
      }

      const cachedData = localStorage.getItem(this.cacheKeys.make);
      const cachedTimestamp = localStorage.getItem(this.cacheKeys.makeTimestamp);

      if (cachedData && cachedTimestamp) {
        const now = Date.now();
        const cacheAge = now - parseInt(cachedTimestamp, 10);

        if (cacheAge < this.cacheExpiration) {
          return Promise.resolve(JSON.parse(cachedData));
        } else {
          localStorage.removeItem(this.cacheKeys.make);
          localStorage.removeItem(this.cacheKeys.makeTimestamp);
        }
      }
    } catch (error) {
      console.error('Error reading cached Make data:', error);
    }

    return null;
  }

  setCachedMakeData(data) {
    try {
      const cacheData = {
        data: data.data,
        status: data.status,
        extra_data: data.extra_data || [],
      };
      localStorage.setItem(this.cacheKeys.make, JSON.stringify(cacheData));
      localStorage.setItem(this.cacheKeys.makeTimestamp, Date.now().toString());
    } catch (error) {
      console.error('Error caching Make data:', error);
    }
  }

  getCarData(url, params) {
    let timeout = setTimeout(() => {
      this.carSelectorElement.classList.add('updating');
      this.preloader.style.display = 'flex';
    }, 500);

    let data = fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Shopify-Shop-Domain': document.querySelector('.voo-main-product[data-domain]').getAttribute('data-domain'),
      },
      body: JSON.stringify(params),
    }).finally(() => {
      clearTimeout(timeout);
      this.carSelectorElement.classList.remove('updating');
      this.preloader.style.display = 'none';
    });

    return data;
  }

  addToCart() {
    let items = [];
    const variantsData = JSON.parse(document.querySelector('#variants_json[type="application/json"]').textContent);

    document
      .getElementById('current-variant')
      .value.split(',')
      .forEach((item, i) => {
        let barcode = '';
        variantsData.forEach((variant) => {
          if (variant.id === Number(document.getElementById('current-variant').value)) {
            barcode = variant.barcode;
          }
        });

        if (barcode === '') {
          const variantsDataFromEquipment = JSON.parse(
            document.querySelector('.product-type-switcher.selected script')?.textContent,
          );
          if (variantsDataFromEquipment.length === 2) {
            variantsDataFromEquipment.forEach((variantFromEquipment) => {
              variantFromEquipment.forEach((variant) => {
                if (variant.id == item) {
                  barcode = variant.barcode;
                }
              });
            });
          }
        }

        items.push({
          id: Number(item),
          quantity: 1,
          properties: {
            'Car Model': document.querySelector('.voo-product-selected').innerText,
            _locale: document.querySelector('html').getAttribute('lang'),
            _currenturl: window.location.origin + window.location.pathname,
            _barcode: barcode,
            '_Order Status': 'Jetzt Bestellen -',
            _order_status: 'order',
            '_Knieschoner gratis': 'Knieschoner gratis',
          },
        });
        // Set options
        ['textur', 'matte_farbe', 'einfassung'].forEach((option) => {
          let optionEnglish;
          switch (option) {
            case 'textur':
              optionEnglish = 'texture';
              break;
            case 'matte_farbe':
              optionEnglish = 'mat_color';
              break;
            case 'einfassung':
              optionEnglish = 'border_color';
              break;
            default:
              optionEnglish = option;
          }
          items[i].properties[`_${optionEnglish}`] = document.querySelector(
            `input.${option}-value-input:checked`,
          ).dataset.color;
        });

        // Set parametrs
        const carParams = {
          make: '',
          model: '',
          generation: '',
          body_type: '',
          transmission: '',
        };
        ['make', 'model', 'generation', 'body_type', 'transmission'].forEach((type) => {
          const value = document.querySelector(`#voo-car-${type}`)?.dataset.value || '';
          carParams[type] = value;
          items[i].properties[`_${type}`] = value;
        });

        if (carParams.generation === 'notSure') {
          carParams.body_type = 'notSure';
          carParams.transmission = 'notSure';
          items[i].properties['_body_type'] = 'notSure';
          items[i].properties['_transmission'] = 'notSure';
        }

        const customCarParams = { ...this.customCarParameters };
        ['make', 'model', 'generation', 'body_type', 'transmission'].forEach((type) => {
          if (carParams[type] === 'notSure') {
            customCarParams[type] = 1;
          }
        });

        items[i].properties['_custom_car'] = JSON.stringify(customCarParams);

        // if have additional parameters
        let additionalParams = {};
        document.querySelectorAll('.additional-option-field')?.forEach((additional) => {
          const checkedInput = additional.querySelector('input:checked');
          if (!checkedInput) return;

          additionalParams[checkedInput.dataset.attributeId] = checkedInput.dataset.attributeValue;

          const propertyKey = additional.firstElementChild?.textContent?.trim();
          const propertyValue = additional.querySelector('.choosed')?.textContent?.trim() || checkedInput.dataset.name;
          if (propertyKey && propertyValue) {
            items[i].properties[propertyKey] = propertyValue;
          }
        });

        let addParamsIds = {};
        document.querySelectorAll('.voo-add-data .step').forEach((step) => {
          const stepID = step.dataset.stepId;
          const optionValue = step.querySelector('input:checked')?.value || '';
          if (stepID) addParamsIds[stepID] = optionValue;

          if (step.style.display === 'none') return;

          const checkedInput = step.querySelector('input:checked');
          if (!checkedInput) return;

          const propertyKey = step.dataset.stepName || step.querySelector('.add-data__title')?.textContent?.trim();
          const propertyValue =
            checkedInput.dataset.name || step.querySelector('.selected-option')?.textContent?.trim();
          if (propertyKey && propertyValue) {
            items[i].properties[propertyKey] = propertyValue;
          }
        });

        if (JSON.stringify(additionalParams) !== '{}')
          items[i].properties['_additional_options'] = JSON.stringify(additionalParams);
        if (JSON.stringify(addParamsIds) !== '{}') {
          items[i].properties['_additional_car_data'] = JSON.stringify(addParamsIds);
        }
      });

    let formData = {
      items: items,
      sections: this.getSectionsToRender().map((section) => section.id),
    };

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.sections = cartItemComponentsSectionIds.join(',');
    });

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((response) => {
        if (this.cartDrawer && this.cartDrawer.classList.contains('is-empty'))
          this.cartDrawer.classList.remove('is-empty');
        sectionRenderer.renderSection(this.cartItemsId, { cache: false });
        this.cartDrawer.open();
      });
  }

  getAndUpdateAdditionalCarParams(url) {
    if (!(document.querySelector('.voo-additional-data') instanceof HTMLElement)) return;
    const locale = document.documentElement.lang || 'en';
    fetch(this.apiUrl + 'car-attribute?' + `locale=${locale}&` + url)
      .then((responce) => responce.json())
      .then((data) => {
        data.data?.car_attributes.forEach((item) => {
          if (item.id !== 2) {
            let loverOptionsName = item.original_label ? item.original_label : item.label;
            loverOptionsName = loverOptionsName.toLowerCase().split(' ').join('_');
            let optionsMarkUp = '';
            let checkedLabel = '';
            item.options.forEach((option) => {
              if (option.is_checked) checkedLabel = option.label;
              let optionMarkUp = `<input type="radio" name="properties[${loverOptionsName}]" value="${option.label}" data-name="${option.label}" color="${option.label}" id="${loverOptionsName}-${option.id}" required data-attribute-id="${item.id}" data-attribute-value="${option.id}" ${option.is_checked ? 'checked' : ''}>
            <label for="${loverOptionsName}-${option.id}" style="background: url(&quot;${option.image}&quot;) center center / 100% no-repeat;">${option.label}</label>`;
              optionsMarkUp += optionMarkUp;
            });
            let itemMarkUp = `<span>${item.label}</span>
            <span class="choosed choosed_${loverOptionsName}" data-type-label="${loverOptionsName}">${checkedLabel}</span>
            <span class="product_description">?</span>
            <div class="tooltip_descr">${item.description}</div>
            <div class="radio__wrapper ${loverOptionsName}__radio ${loverOptionsName} ${item.styles} ${item.styles}_radios" data-type="${loverOptionsName}">
              ${optionsMarkUp}
            </div>`;
            let parentDiv = document.createElement('div');
            parentDiv.classList.add('line-item-property__field', 'additional-option-field', loverOptionsName);
            parentDiv.innerHTML = itemMarkUp;
            if (document.querySelector(`.line-item-property__field.${loverOptionsName}`))
              document.querySelector(`.line-item-property__field.${loverOptionsName}`).remove();
            document.querySelector('.voo-additional-data').appendChild(parentDiv);
            parentDiv.addEventListener('change', (e) => {
              e.target.closest('.line-item-property__field').querySelector('.choosed').innerText =
                e.target.dataset.name;
            });
          }
        });
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getAndUpdateAdditionalData(url) {
    this.clearAdditionalData();
    const locale = document.documentElement.lang || 'en';
    const requestUrl = this.apiUrl + 'car-additional-data?' + `locale=${locale}&` + url;

    fetch(requestUrl)
      .then((response) => response.json())
      .then((data) => {
        if (!data.data || data.data.length === 0 || data.data == null) {
          return;
        }
        const productString = document.querySelector('.product-type-switcher.selected #current-product').value;
        const variantIds = productString.split(',').map((id) => Number(id.trim()));
        const productIds = data.data['0']?.product_ids || [];
        if (Array.isArray(productIds) && productIds.length > 0) {
          const hasMatch = variantIds.some((id) => productIds.includes(id));
          if (hasMatch) {
            this.renderAdditionalData(data);
          }
        } else {
          this.renderAdditionalData(data);
        }
      })
      .catch((error) => {
        console.error('Error fetching additional data:', error);
      });
  }

  renderAdditionalData(addData) {
    const wrapper = document.querySelector('.voo-add-data');
    if (!wrapper) {
      console.error('Wrapper .voo-add-data not found');
      return;
    }
    wrapper.innerHTML = '';
    const stepMap = {};
    const renderedStepIds = new Set();

    function renderStep(step, level = 1) {
      if (renderedStepIds.has(step.id)) return;
      renderedStepIds.add(step.id);

      const stepEl = document.createElement('div');
      stepEl.classList.add('step');
      stepEl.dataset.stepId = step.id;
      stepEl.dataset.stepName = step.name || '';
      stepEl.dataset.level = level;
      if (level > 1) stepEl.style.display = 'none';

      const titleEl = document.createElement('span');
      titleEl.classList.add('add-data__title');
      titleEl.textContent = step.name;
      stepEl.appendChild(titleEl);

      const selectedSpan = document.createElement('span');
      selectedSpan.classList.add('selected-option');
      stepEl.appendChild(selectedSpan);

      const optionsWrapper = document.createElement('div');
      optionsWrapper.classList.add('options');

      step.options.forEach((option) => {
        const optionId = `step-${step.id}-option-${option.id}`;
        const inputEl = document.createElement('input');
        inputEl.type = 'radio';
        inputEl.name = `step-${step.id}`;
        inputEl.id = optionId;
        inputEl.value = option.id;
        inputEl.dataset.name = option.name || '';

        const labelEl = document.createElement('label');
        labelEl.htmlFor = optionId;

        if (option.display_type === 0) {
          labelEl.textContent = option.name;
        } else {
          const imgEl = createImageElement(option.image_url, option.name);
          const imgMagnified = createImageElement(option.image_url, option.name);
          imgMagnified.classList.add('magnified-image');
          const icon = createZoomIcon(option.image_url, option.name);
          labelEl.classList.add('label-image');

          labelEl.appendChild(imgEl);
          labelEl.insertAdjacentElement('afterend', imgMagnified);

          if (option.display_type === 2) {
            const span = document.createElement('span');
            span.textContent = option.name;
            labelEl.appendChild(span);
            labelEl.classList.add('label-image-text');
          }

          labelEl.appendChild(icon);
          labelEl.appendChild(icon);
        }

        function createImageElement(src, alt) {
          const img = document.createElement('img');
          img.src = src;
          img.alt = alt;
          return img;
        }

        function createZoomIcon(imageUrl, altText = '') {
          const icon = document.createElement('span');
          icon.classList.add('zoom-icon');
          icon.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 20H4M4 20V16M4 20L8.5 15.5M16 4H20M20 4V8M20 4L15.5 8.5M4 8L4 4M4 4L8 4M4 4L8.5 8.5M20 16L20 20M20 20H16M20 20L15.5 15.5" stroke="#BDBDBD" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          `;

          icon.addEventListener('click', (e) => {
            e.stopPropagation();
            const overlay = document.getElementById('image-preview-overlay');
            const overlayImg = overlay.querySelector('.overlay-img');
            overlayImg.src = imageUrl;
            overlayImg.alt = altText;
            overlay.classList.remove('hidden');
            document.body.classList.add('overflow-hidden');
          });
          return icon;
        }

        optionsWrapper.appendChild(inputEl);
        optionsWrapper.appendChild(labelEl);

        inputEl.addEventListener('change', () => {
          selectedSpan.textContent = option.name;

          Object.values(stepMap).forEach((el) => {
            if (parseInt(el.dataset.level, 10) > level) {
              el.style.display = 'none';
            }
          });

          if (option.children && option.children.length > 0) {
            option.children.forEach((childStep) => {
              const childEl = stepMap[childStep.id];
              if (childEl) childEl.style.display = '';
            });
          }
        });
      });

      document.getElementById('image-preview-overlay').addEventListener('click', () => {
        document.getElementById('image-preview-overlay').classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
      });

      stepEl.appendChild(optionsWrapper);
      wrapper.appendChild(stepEl);
      stepMap[step.id] = stepEl;

      step.options.forEach((option) => {
        if (option.children && option.children.length > 0) {
          option.children.forEach((childStep) => {
            renderStep(childStep, level + 1);
          });
        }
      });
    }
    addData.data?.forEach((step) => renderStep(step, 1));
  }

  clearAdditionalData() {
    const wrapper = document.querySelector('.voo-add-data');
    if (wrapper) wrapper.innerHTML = '';
  }

  async init() {
    this.updateProgressBar(0, this.totalSteps);
    // Update type of car
    // Check by pathname what type of car
    for (const key in this.dynamicProducts) {
      if (window.location.pathname.split('products/')[1] == this.dynamicProducts[key]) {
        document.querySelector('.voo-main-product[data-domain]')?.setAttribute('data-type', `${key}`);
      }
    }

    // Init car selectors
    await this.updateSelectors('voo-car-make');
    await this.updateSelectors('voo-car-model');
    await this.updateSelectors('voo-car-generation');
    await this.updateSelectors('voo-car-body_type');
    await this.updateSelectors('voo-car-transmission');
    await this.updateSelectors(
      document.getElementById('voo-car-make'),
      document.getElementById('voo-car-make').dataset.type,
    );

    // Show car selectors and hide preloader
    if (!this.searchParams.get('make')) {
      this.carSelectorElement.style.display = 'flex';
      this.preloader.style.display = 'none';
      this.handleSelectorLoading(false);
    }

    // Event to open car selectors
    if (document.querySelector('.voo-product-change')) {
      document.querySelector('.voo-product-change')?.addEventListener('click', () => {
        this.clearSelectors();
        this.updateProgressBar(0, this.totalSteps);
        document.querySelector('#voo-car-make + .nice-select').classList.remove('error-data');
        document.querySelectorAll('.voo-product-selected').forEach((title) => (title.innerHTML = ''));
        document.querySelectorAll('.nice-select li.selected').forEach((el) => el.classList.remove('selected'));
        this.toggleAddToCartButton(true);
        const productControls = document.querySelectorAll('.voo-product-controls');
        if (productControls.length > 0) {
          productControls.forEach((control) => control.setAttribute('disabled', 'true'));
        }
        const notSureMsg = document.querySelector('.not-sure-message');
        if (notSureMsg) notSureMsg.style.display = 'none';
      });
    }

    this.variants[0].closest('.section-variants').addEventListener(
      'click',
      (e) => {
        if (e.target instanceof HTMLInputElement && e.target.type === 'radio') {
          e.stopPropagation();
        }
      },
      true,
    );

    // Clicking a swatch <label> fires its real click event, then the browser dispatches a
    // second synthetic click on the associated radio <input>. Both bubble to document, so
    // every analytics document.onclick listener (FB Pixel, GTM, Google Ads, TikTok) runs
    // twice per swatch interaction. Stop the radio's bubble here so those handlers only run once.
    this.variants[0].closest('.section-variants').addEventListener(
      'click',
      (e) => {
        if (e.target instanceof HTMLInputElement && e.target.type === 'radio') {
          e.stopPropagation();
        }
      },
      true,
    );

    // Event on variants
    this.variants[0].closest('.section-variants').addEventListener('change', (e) => {
      // --- Synchronous visual feedback ----------------------------------------------------
      // Cheap DOM writes that change what the user *sees* right after their click.
      // Kept out of the deferred block below so the swatch outline, texture preview, and
      // option-label text update in the very next paint — not after rAF + setTimeout.

      if (e.target.hasAttribute('name') && e.target.getAttribute('name') == 'Textur') {
        const newOptionValue = e.target.value.toLowerCase();
        document.querySelector('.variant-values[data-texture]')?.setAttribute('data-texture', newOptionValue);
      }

      // Update the option labels next to each option title from the currently checked input.
      // Reads directly from radios — no variants_json lookup needed.
      this.variants.forEach((publicOption) => {
        const checked = /** @type {HTMLInputElement | null} */ (publicOption.querySelector('input:checked'));
        const labelEl = /** @type {HTMLElement | null | undefined} */ (
          publicOption.previousElementSibling?.querySelector('.selected-option-label')
        );
        if (checked && labelEl) labelEl.innerText = checked.value;
      });

      // --- Deferred heavy work -----------------------------------------------------------
      // Anything that touches the gallery, network, history, localStorage, or large JSON
      // stays here so it runs after the visual paint, off the input-event task.

      this._lastChangeIsTrusted = this._lastChangeIsTrusted || false || e.isTrusted;
      if (this._pendingVariantUpdate) return;
      this._pendingVariantUpdate = true;
      this._variantUpdateAbort?.abort();
      this._variantUpdateAbort = new AbortController();

      // requestAnimationFrame fires BEFORE the next paint — putting heavy work directly inside
      // makes the paint wait for it, inflating INP. Wrap in setTimeout(...,0) so the heavy
      // work runs as a fresh task AFTER the browser has had a chance to repaint.
      requestAnimationFrame(() =>
        setTimeout(() => {
          this._pendingVariantUpdate = false;
          const isTrusted = this._lastChangeIsTrusted;
          this._lastChangeIsTrusted = false;

          if (this.variantImageId && isTrusted) this.mediaGallery.setActiveMedia(this.variantImageId, false);

          this.setLocalStorageOptions();
          const variantsData = this.getVariantsData();
          const variantsDataFromEquipment = this.getEquipmentVariantsData();
          const currentVariant = document.getElementById('current-variant').value;

          let variantTitle = '';

          // Write a variantTitle by options
          this.variants.forEach((publicOption, i) => {
            variantTitle +=
              i == this.variants.length - 1
                ? publicOption.querySelector('input:checked').value
                : publicOption.querySelector('input:checked').value + ' / ';
          });

          let bundleIDs = '';
          let newVariantId = '';

          if (variantsDataFromEquipment?.length === 2) {
            // Find ids for bundle
            variantsDataFromEquipment.forEach((variantFromEquipment) => {
              variantFromEquipment.forEach((variant) => {
                if (variantTitle == variant.title) {
                  bundleIDs += bundleIDs == '' ? variant.id : ',' + variant.id;
                }
              });
            });
          }

          variantsData.forEach((variant) => {
            if (variant.title == variantTitle) {
              newVariantId = variant.id;

              // if variant the same -> return
              if (!newVariantId || newVariantId == currentVariant) return;

              // Update URL
              this.searchParams.set('variant', newVariantId);
              window.history.replaceState(
                {},
                '',
                window.location.origin + window.location.pathname + `?${this.searchParams.toString()}`,
              );
            }
          });
          // Update current variant to variant or bundle
          bundleIDs !== ''
            ? (document.getElementById('current-variant').value = bundleIDs)
            : (document.getElementById('current-variant').value = newVariantId);

          // Update dynamic images to new variant
          document.querySelectorAll('li.voo-dynamic-image').forEach((item) => {
            this.updateImageSrc(item.querySelector('img'));
          });
        }, 0),
      );
    });

    // Event when focusout from custom select
    this.carSelectorElement.addEventListener('focusout', (e) => {
      if (e.target.classList.contains('nice-select-search') && e.target.value == '') {
        let defaultSelectId = e.target.closest('.nice-select').previousElementSibling.id;
        const type = e.target.closest('.nice-select').previousElementSibling.dataset.type;
        this.carSelectors[type].update();
        document.getElementById(defaultSelectId).nextElementSibling.classList.remove('error-data');
        document
          .getElementById(defaultSelectId)
          .nextElementSibling.querySelector('li.option.selected:not(.disabled)')
          ?.classList.remove('selected');
        this.clearSelectors(type);
      }
    });

    document.querySelector('.voo-back-data')?.addEventListener('keydown', (e) => {
      function addToList(target) {
        let inputField = target.value;
        const selectDefault = target.closest('.nice-select').previousElementSibling;
        const type = selectDefault.id.split('voo-car-')[1];
        let newOption = document.createElement('option');
        newOption.innerText = inputField;
        newOption.classList.add('custom');
        selectDefault.querySelector('option.hidden').after(newOption);
        // Update selector and clear others
        this.carSelectors[type].update();
        this.clearSelectors(type);

        // Update custom paramets
        this.customCarParameters[type] = 1;

        // Select new param
        selectDefault.nextElementSibling.classList.remove('error-data');
        selectDefault.nextElementSibling.querySelector(`.option[data-value="${inputField}"]`).click();
      }

      if (
        e.key == 'Enter' &&
        e.target.closest('.nice-select').querySelector('ul.list:not(.empty)') &&
        e.target.closest('.nice-select:not(.error-data)')?.querySelector('ul li.option:not(.disabled)')
      ) {
        if (
          e.target.closest('.nice-select').querySelectorAll('ul.list .option:not(.hidden):not(.disabled)').length > 0
        ) {
          e.target.closest('.nice-select').querySelectorAll('ul.list .option:not(.hidden):not(.disabled)')[0].click();
        } else {
          let updateSelect = addToList.bind(this);
          updateSelect(e.target);
        }
      }
    });
    // Event for car data selectors click
    document.querySelector('.voo-back-data')?.addEventListener('click', (e) => {
      function checkImage(imageSrc, onImageExist, onMissingImage) {
        let img = new Image();
        img.onload = onImageExist;
        img.onerror = onMissingImage;
        img.src = imageSrc;
      }

      const nextStepSelect = e.target?.closest('.nice-select')?.nextElementSibling;
      const currentSelect = e.target?.closest('.nice-select').dataset.step;

      if (
        e.target instanceof HTMLElement &&
        (e.target.classList.contains('option') ||
          e.target.classList.contains('search-submit') ||
          e.target.dataset.notSure == 'true')
      ) {
        e.stopPropagation();
      }

      setTimeout(() => {
        if (e.target instanceof HTMLElement && e.target.dataset.notSure == 'true') {
          this.handleNotSureClick(e.target);
        } else if (e.target.classList.contains('search-submit')) {
          // If click to add car param that not exsist
          const select = e.target.closest('.nice-select');
          const selectDefault = select?.previousElementSibling;
          let inputField = select.querySelector('.nice-select-search').value;
          const type = selectDefault.id.split('voo-car-')[1];

          // Update selector and clear others
          this.carSelectors[type].update();
          this.clearSelectors(type);

          // Update custom paramets
          this.customCarParameters[type] = 1;

          // Select new param
          selectDefault.nextElementSibling.classList.remove('error-data');
          selectDefault.nextElementSibling.querySelector(`.option[data-value="${inputField}"]`).click();

          if (type == 'transmission') {
            let titleParams = '';

            // Update search parameters and set title
            ['make', 'model', 'generation', 'body_type', 'transmission'].forEach((type, i) => {
              const selectedTypeParam = document.getElementById(`voo-car-${type}`).dataset.value;
              if (selectedTypeParam) {
                this.searchParams.set(type, selectedTypeParam);
              }
              const optionTitle = document.querySelector(
                `#voo-car-${type} + .nice-select li.selected:not(.disabled)`,
              ).innerHTML;
              if (optionTitle) titleParams += i === 4 ? optionTitle : optionTitle + ' ';
            });

            // Update title
            document.querySelectorAll('.voo-product-selected').forEach((title) => (title.innerHTML = titleParams));

            // Enable add to cart button
            this.toggleAddToCartButton(false);
            const productControls = document.querySelectorAll('.voo-product-controls');
            if (productControls.length > 0) {
              productControls.forEach((control) => control.removeAttribute('disabled'));
            }

            // Update url
            window.history.replaceState(
              {},
              '',
              window.location.origin + window.location.pathname + `?${this.searchParams.toString()}`,
            );

            // Additional car parameters (example: VAN Arrangement)
            this.getAndUpdateAdditionalCarParams(this.searchParams.toString());
            this.getAndUpdateAdditionalData(this.searchParams.toString());
          } else {
            this.clearAdditionalData();

            // Disable add to cart button
            this.toggleAddToCartButton(true);
            const productControls = document.querySelectorAll('.voo-product-controls');
            if (productControls.length > 0) {
              productControls.forEach((control) => control.setAttribute('disabled', 'true'));
            }
            document.querySelectorAll('.voo-product-selected').forEach((title) => (title.innerHTML = ''));
          }
        } else if (e.target.classList.contains('option') && e.target.dataset.value) {
          // if click to option

          const currentSelectType = e.target.closest('.nice-select').previousElementSibling?.dataset.type;
          if (e.target.closest('.nice-select').previousElementSibling) {
            e.target
              .closest('.nice-select')
              .previousElementSibling.setAttribute('data-value', `${e.target.dataset.value}`);
          }

          if (currentSelectType === 'generation' && e.target.dataset.value !== 'notSure') {
            const bodyTypeSelector = document.querySelector('#voo-car-body_type')?.nextElementSibling;
            const transmissionSelector = document.querySelector('#voo-car-transmission')?.nextElementSibling;

            if (bodyTypeSelector) {
              bodyTypeSelector.classList.remove('not-sure-selected');
              const bodyTypeNotSureOption = bodyTypeSelector.querySelector('[data-value="notSure"]');
              if (bodyTypeNotSureOption) {
                bodyTypeNotSureOption.classList.remove('selected');
                bodyTypeNotSureOption.classList.add('disabled');
              }
              this.customCarParameters.body_type = 0;
            }

            if (transmissionSelector) {
              transmissionSelector.classList.remove('not-sure-selected');
              const transmissionNotSureOption = transmissionSelector.querySelector('[data-value="notSure"]');
              if (transmissionNotSureOption) {
                transmissionNotSureOption.classList.remove('selected');
                transmissionNotSureOption.classList.add('disabled');
              }
              this.customCarParameters.transmission = 0;
            }
          }

          // Clear all old data value
          let clearValues = false;
          ['make', 'model', 'generation', 'body_type', 'transmission'].forEach((type, i) => {
            if (
              e.target.closest('.nice-select').previousElementSibling &&
              e.target.closest('.nice-select').previousElementSibling.dataset.type == type
            ) {
              clearValues = true;
            } else if (clearValues) {
              document.querySelector(`#voo-car-${type}`).removeAttribute('data-value');
            }
          });

          for (const key in this.customCarParameters) {
            if (
              e.target.closest('.nice-select').previousElementSibling &&
              key == e.target.closest('.nice-select').previousElementSibling.dataset.type &&
              Number(e.target.dataset.value) &&
              !e.target.classList.contains('voo_custom')
            ) {
              this.customCarParameters[key] = 0;
            }
          }

          e.target
            .closest('.nice-select ul')
            .querySelectorAll('.hidden')
            .forEach((element) => {
              element.classList.remove('hidden');
            });
          this.toggleNotSureMessage(false);

          if (nextStepSelect) {
            // If have next select (not last option)
            this.updateSelectors(nextStepSelect, nextStepSelect.dataset.type, e.target.dataset.value);
            this.clearAdditionalData();
            this.updateProgressBar(currentSelect, this.totalSteps);
            e.target?.closest('.nice-select').classList.remove('not-sure-selected');

            const isProgrammaticClick = !e.isTrusted || (this.carSelectorsUpdating && !this.searchParamsLoaded);
            if (isProgrammaticClick) {
              const currentSelectType = e.target.closest('.nice-select').previousElementSibling?.dataset.type;
              if (currentSelectType) {
                this.searchParams?.set(currentSelectType, e.target.dataset.value);
              }
            } else {
              this.updateSearchParams();
            }

            // Disable add to cart button
            this.toggleAddToCartButton(true);
            const productControls = document.querySelectorAll('.voo-product-controls');
            if (productControls.length > 0) {
              productControls.forEach((control) => control.setAttribute('disabled', 'true'));
            }
            document.querySelectorAll('.voo-product-selected').forEach((title) => (title.innerHTML = ''));
          } else {
            // If dont have last select (last option);
            this.updateProgressBar(currentSelect, this.totalSteps);
            let dynamicImgUrl = 'https://admin.primeeva.com/api/car-template?';
            const imageToChange = document
              .querySelector('ul.product__media-list')
              .querySelector('li:nth-of-type(3) img');
            const imageToChangeThumbnail = document
              .querySelector('ul.thumbnail-list')
              .querySelector('li:nth-of-type(3) img');

            // Hide preloader
            this.preloader.style.display = 'none';
            this.carSelectorElement.style.display = 'flex';
            this.handleSelectorLoading(false);

            let titleParams = '';
            // Update search parameters and set title
            ['make', 'model', 'generation', 'body_type', 'transmission'].forEach((type, i) => {
              this.searchParams.set(type, document.getElementById(`voo-car-${type}`).dataset.value);
              const optionTitle = document.querySelector(
                `#voo-car-${type} + .nice-select li.selected:not(.disabled)`,
              )?.innerHTML;
              if (optionTitle) titleParams += i === 4 ? optionTitle : optionTitle + ' ';
              dynamicImgUrl += type + '=' + document.getElementById(`voo-car-${type}`).dataset.value + '&';
            });
            dynamicImgUrl.slice(0, -1);

            // If data was loaded from url, prevent new reload
            this.searchParamsLoaded = true;

            // Update title
            document.querySelectorAll('.voo-product-selected').forEach((title) => (title.innerHTML = titleParams));

            // Enable add to cart button
            this.toggleAddToCartButton(false);
            const productControls = document.querySelectorAll('.voo-product-controls');
            if (productControls.length > 0) {
              productControls.forEach((control) => control.removeAttribute('disabled'));
            }

            // scroll add to cart button to center of screen on mobile
            if (window.matchMedia('(max-width: 750px)').matches) {
              this.addToCartButton?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }

            // Update configuration image to dynamic
            const changeImgSrc = (src) => {
              if (imageToChange) {
                imageToChange.src = src;
                imageToChange.srcset = src;
                imageToChangeThumbnail.src = src;
                imageToChangeThumbnail.srcset = src;
              }

              if (this.magnifyImageEl) {
                this.magnifyImageEl.src = src;
                this.magnifyImageEl.srcset = src;
              }
            };

            checkImage(
              dynamicImgUrl,
              () => changeImgSrc(dynamicImgUrl),
              () => changeImgSrc(this.defaultConfigImgSrc),
            );

            // Update url
            window.history.replaceState(
              {},
              '',
              window.location.origin + window.location.pathname + `?${this.searchParams.toString()}`,
            );

            if (this.needToRedirect.redirect && !this.carSelectorsUpdating) {
              window.location.href =
                window.location.origin +
                `/products/${this.needToRedirect.redirect_url}?${this.searchParams.toString()}`;
            }

            // Additional car parameters (example: VAN Arrangement)
            this.getAndUpdateAdditionalCarParams(this.searchParams.toString());
            this.getAndUpdateAdditionalData(this.searchParams.toString());
          }
        }
      }, 0);
    });

    // Event add to Cart
    this.addToCartButton?.addEventListener('click', (e) => {
      e.preventDefault();
      if (!e.target.hasAttribute('disabled')) {
        this.addToCart();
      } else {
        this.openSelectorByStep(this.currentStep);
      }
    });

    document.querySelector('.car-switcher').addEventListener('click', (e) => {
      if (e.target.tagName === 'A') e.preventDefault();

      if (e.target.href && !this.carSelectorsUpdating && e.target.dataset.disabled != 'true') {
        ['make', 'model', 'generation', 'body_type', 'transmission'].forEach((type) => {
          if (document.getElementById(`voo-car-${type}`).dataset.value) {
            this.searchParams.set(type, document.getElementById(`voo-car-${type}`).dataset.value);
          } else {
            this.searchParams.delete(type);
          }
        });

        window.location.href = e.target.href + '?' + this.searchParams.toString();
      }
    });
    document.querySelector('.section-variants').dispatchEvent(new Event('change'));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Update variant option labels
  JSON.parse(document.querySelector('#variants_json[type="application/json"]').textContent).forEach((variant) => {
    if (variant.id == document.getElementById('current-variant').value) {
      document.querySelectorAll('.section-variants .variant-values').forEach((publicVariant, i) => {
        publicVariant.previousElementSibling.querySelector('.selected-option-label').innerText = variant.options[i];
      });
    }
  });

  // Update bundle id
  let variantTitle = '';
  document.querySelectorAll('.section-variants .variant-values').forEach((publicOption, i) => {
    variantTitle +=
      i == document.querySelectorAll('.section-variants .variant-values').length - 1
        ? publicOption.querySelector('input:checked').value
        : publicOption.querySelector('input:checked').value + ' / ';

    // if texture updated -> update mat colors
    if (publicOption.dataset.option && publicOption.dataset.option == 'Textur') {
      const newOptionValue = publicOption.querySelector('input:checked').value.toLowerCase();
      document.querySelector('.variant-values[data-texture]').setAttribute('data-texture', newOptionValue);
    }
  });
  let bundleIDs = '';
  const variantsDataFromEquipment = JSON.parse(
    document.querySelector('.product-type-switcher.selected script')?.textContent,
  );
  if (variantsDataFromEquipment.length === 2) {
    variantsDataFromEquipment.forEach((variantFromEquipment) => {
      variantFromEquipment.forEach((variant) => {
        if (variantTitle == variant.title) {
          bundleIDs += bundleIDs == '' ? variant.id : ',' + variant.id;
        }
      });
    });
  }
  if (bundleIDs !== '') document.getElementById('current-variant').value = bundleIDs;

  // Init script
  let productApiData = new ProductAPIData();
  productApiData
    .init()
    .then(() => {
      setTimeout(() => {
        const originalButton = document.querySelector('.voo-main-product__info > a.add-to-cart');
        if (originalButton) {
          if (window.stickyAddToCartInstance) {
            window.stickyAddToCartInstance.updateOptions({
              productApiData,
              buttonSelector: '.voo-main-product__info > a.add-to-cart',
            });
          } else {
            window.stickyAddToCartInstance = new StickyAddToCart({ productApiData });
          }
        }
      }, 100);
    })
    .catch(() => {
      setTimeout(() => {
        const originalButton = document.querySelector('.voo-main-product__info > a.add-to-cart');
        if (originalButton) {
          if (window.stickyAddToCartInstance) {
            window.stickyAddToCartInstance.updateOptions({
              productApiData,
              buttonSelector: '.voo-main-product__info > a.add-to-cart',
            });
          } else {
            window.stickyAddToCartInstance = new StickyAddToCart({ productApiData });
          }
        }
      }, 500);
    });
});
